import React, { useState, useEffect } from 'react';
import styles from './SettingsTab.module.css';
import { useTranslation } from 'react-i18next';
import { useFirestoreDoc } from '../../../hooks/useFirestoreDoc';
import { useNotifications } from "../../../utils/NotificationContext/NotificationContext";
import { getAuth, updatePassword } from "firebase/auth";
import Button from '../../ui/Button';

import {
  HiEnvelope, HiPhone, HiKey, HiLockClosed, HiBell, HiGlobeAlt, HiMiniCheckCircle 
} from 'react-icons/hi2';


export default function SettingsTab({ user }) {
  const { docData, loading, error, update } = useFirestoreDoc(`users/${user?.uid}`);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [password, setPassword] = useState('');
  const { t } = useTranslation();
  const { notify } = useNotifications();
  
  const privacyOptions = ['Public', 'Friends Only', 'Private'];
  const languages = ['English', 'Spanish', 'French', 'German'];

   const validatePassword = (password) => {
	  const minLength = 8;
	  const upperCase = /[A-Z]/;
	  const number = /\d/;
	  const specialChar = /[!@#$%^&*()_+[\]{};':"\\|,.<>/?]/;

	  if (password.length < minLength) return false;
	  if (!upperCase.test(password)) return false;
	  if (!number.test(password)) return false;
	  if (!specialChar.test(password)) return false;

	  return true;
	}

  useEffect(() => { if (docData) setForm(docData); }, [docData]);

  useEffect(() => {
    if (!editing) return;
    const timer = setTimeout(async () => {
      await update(form);
      notify('info', t('autosave.saved'));
    }, 1000);
    return () => clearTimeout(timer);
  }, [form, editing]);

  if (loading) return <div className={styles.message}>{t('settings.loading')}</div>;
  if (error) return <div className={styles.error}>{t('settings.error')}</div>;
  if (!form) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePasswordChange = () => {
    if (!password) {
      notify('error', t('settings.enterPassword'));
      return;
    }

	// ? Password strength check
	if (!validatePassword(password)) {
	  notify('error', 'Password too weak. Minimum 8 characters, 1 uppercase, 1 number, 1 special character.');
	  return;
	}
	
	updatePassword(user, password).then(() => {
	    notify('success', t('settings.passwordChanged'));
	}).catch((error) => {
	  notify('error', error.message);
	});
  };

  return (
	<div className={styles.wrapper}>
	<div className={styles.background}></div>
    <div className={styles.card}>
      <h3 className={styles.heading}>{t('settings.title')}</h3>
      <p className={styles.summary}>{t('settings.summary')}</p>

      <div className={styles.section}>
        <div className={styles.field}><HiEnvelope className={styles.icon} /> <label>{t('settings.email')}</label></div>
        <span>{form.email || '-'}</span>

        <div style={{paddingTop: "2rem"}}className={styles.field}><HiPhone className={styles.icon} /> <label>{t('settings.phone')}</label></div>
        {editing ? <input name="phone" value={form.phone || ''} onChange={handleChange} /> : <span>{form.phone || '-'}</span>}
      </div>

      <div className={styles.section}>
        <div className={styles.field}><HiKey className={styles.icon} /> <label>{t('settings.password')}</label></div>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" />
        <Button variant={'secondary'} className={styles.actionBtn} onClick={handlePasswordChange}><HiLockClosed /> {t('settings.changePassword')}</Button>
      </div>

      <div className={styles.section}>
        <div className={styles.field}><HiLockClosed className={styles.icon} /> <label>{t('settings.privacy')}</label></div>
        {editing ? (
          <select name="privacy" value={form.privacy || ''} onChange={handleChange}>
            <option value="">{t('profile.select')}</option>
            {privacyOptions.map(p => <option key={p} value={p}>{t(`settings.privacyOptions.${p}`)}</option>)}
          </select>
        ) : (
          <span>{form.privacy || '-'}</span>
        )}
      </div>

      <div className={styles.section}>
        <div className={styles.field}><HiBell className={styles.icon} /> <label>{t('settings.notifications')}</label></div>
        {['debateInvites', 'replies', 'moderatorRequests'].map(key => (
          <div key={key} className={styles.checkbox}>
            <input type="checkbox" name={key} checked={form[key] || false} onChange={handleChange} disabled={!editing} />
            <span>{t(`settings.notifications.${key}`)}</span>
          </div>
        ))}
      </div>

      <div className={styles.section}>
        <div className={styles.field}><HiGlobeAlt className={styles.icon} /> <label>{t('settings.language')}</label></div>
        {editing ? (
          <select name="language" value={form.language || ''} onChange={handleChange}>
            <option value="">{t('profile.select')}</option>
            {languages.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        ) : (
          <span>{form.language || '-'}</span>
        )}
      </div>

      <div className={styles.actions}>
        {editing ? (
          <Button variant={'secondary'} className={styles.cancelBtn} onClick={() => setEditing(false)}>
            <HiMiniCheckCircle  /> {t('profile.done')}
          </Button>
        ) : (
          <Button variant={'secondary'} className={styles.editBtn} onClick={() => setEditing(true)}>
            {t('profile.edit')}
          </Button>
        )}
      </div>
    </div>
	</div>
  );
}


